import { Box, Button, FormControlLabel, Stack, Switch } from "@mui/material";
import { FC, useCallback, useEffect, useState, useMemo } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetPartnerDropdownsQuery } from "src/shared/api/dropdowns/partner";
import { useGetPermissionsQuery } from "src/shared/api/rbac/rbac";
import { SelectField, ModalComponent } from "src/shared/components";
import { agenciesCountry } from "src/shared/constants/company.constants";
import { routes } from "src/shared/constants/routes.constants";
import {
  EAgencies,
  ECountries,
  INameValue,
} from "src/shared/types/common.types";
import { EOrganizationValue } from "src/shared/types/companies.types";
import { messages } from "src/shared/utils/hookFormRules";
import { IPartnerInitFormValues } from "../model/types/partner";

interface INewPartnerInitialModalProps {
  open: boolean;
  onClose: () => void;
}

const EMPTY_AGENCY_VALUE = "empty";

export const NewPartnerInitialModal: FC<INewPartnerInitialModalProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const [ownership_id, setOwnersipId] = useState<number | null>(null);

  const { data: dropdowns } = useGetPartnerDropdownsQuery();
  const {
    country,
    agency,
    organization_form: organization_form_list,
    ownership,
  } = dropdowns || {};
  const { data: permissionData } = useGetPermissionsQuery(undefined);
  const allowedAgencies = permissionData?.data?.allowed_agencies;

  const filteredAgency = useMemo(
    () =>
      agency?.filter(({ value }) => allowedAgencies?.includes(Number(value))) ||
      [],
    [agency, allowedAgencies],
  );

  const agencyOptions: INameValue<EAgencies | typeof EMPTY_AGENCY_VALUE>[] =
    filteredAgency.length > 0
      ? filteredAgency
      : [
          {
            name: "Нет доступных компаний",
            value: EMPTY_AGENCY_VALUE,
          },
        ];

  const hasAvailableAgencies = filteredAgency.length > 0;

  const methods = useForm<IPartnerInitFormValues>({
    defaultValues: {
      registration_country_id: ECountries.BY,
      agency: EAgencies.Webcom_BY,
      organization_form: EOrganizationValue.LegalEntity,
      is_resident: false,
    },
    mode: "onChange",
  });
  const { control, setValue, handleSubmit } = methods;

  const submitForm = (data: IPartnerInitFormValues) => {
    const { registration_country_id, is_resident, organization_form, agency } =
      data;
    const params = new URLSearchParams({
      agency: String(agency),
      is_resident: String(is_resident ? 1 : 0),
      registration_country_id: String(registration_country_id),
      organization_form: String(organization_form),
      ownership_id: String(ownership_id || "null"),
    }).toString();
    navigate(`${routes.newPartner}?${params}`);
  };

  const organization_form = useWatch({
    control,
    name: "organization_form",
  });

  const partnerAgency = useWatch({
    control,
    name: "agency",
  });

  const registrationCountry = useWatch({
    control,
    name: "registration_country_id",
  });

  const handleOrgFormChange = useCallback(
    (form: EOrganizationValue | null) => {
      switch (form) {
        case EOrganizationValue.IndividualEntrepreneur: {
          const individualInterprenerOwnershipValue = ownership?.find(
            ({ name }) =>
              name.toLowerCase() === "индивидуальный предприниматель",
          );

          setOwnersipId(individualInterprenerOwnershipValue?.value || 8);
          break;
        }
        case EOrganizationValue.Individual: {
          const individualOwnershipValue = ownership?.find(
            ({ name }) => name.toLowerCase() === "физическое лицо",
          );
          setOwnersipId(individualOwnershipValue?.value || 25);
          break;
        }
        case EOrganizationValue.LegalEntity: {
          setOwnersipId(null);
          break;
        }

        default: {
          setOwnersipId(null);
          break;
        }
      }
    },
    [ownership],
  );

  let reorderedOrganization_form_list: Array<INameValue<number>> = [];

  if (organization_form_list) {
    reorderedOrganization_form_list = [
      organization_form_list[2],
      organization_form_list[1],
      organization_form_list[0],
    ];
  }

  useEffect(() => {
    if (!hasAvailableAgencies) return;

    const currentAgencyExists = filteredAgency.some(
      ({ value }) => value === partnerAgency,
    );

    if (currentAgencyExists) return;

    const firstAgency = filteredAgency[0]?.value as EAgencies;

    if (!firstAgency) return;

    setValue("agency", firstAgency, {
      shouldValidate: true,
    });

    setValue(
      "registration_country_id",
      agenciesCountry[firstAgency] || ECountries.BY,
    );
  }, [filteredAgency, hasAvailableAgencies, partnerAgency, setValue]);

  useEffect(() => {
    if (organization_form) handleOrgFormChange(organization_form);
  }, [organization_form, handleOrgFormChange]);

  useEffect(() => {
    const agencyCountry =
      partnerAgency !== "" ? agenciesCountry[partnerAgency] : null;

    if (registrationCountry === agencyCountry) {
      setValue("is_resident", true);
    } else {
      setValue("is_resident", false);
    }
  }, [partnerAgency, registrationCountry, setValue]);

  return (
    <ModalComponent
      open={open}
      title="Новый контрагент: первоначальная настройка"
      onClose={onClose}
      small
      modalFooter={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={onClose}>
            Отменить
          </Button>
          <Button
            disabled={!hasAvailableAgencies}
            onClick={handleSubmit(submitForm)}
            variant="contained"
            type="submit"
          >
            Применить
          </Button>
        </Box>
      }
    >
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(submitForm)}>
          <Stack spacing={3} sx={{ pt: 3 }}>
            <Box>
              <Controller
                control={control}
                name="agency"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <SelectField<EAgencies | typeof EMPTY_AGENCY_VALUE>
                    inputRef={ref}
                    fullWidth
                    {...field}
                    value={
                      hasAvailableAgencies ? field.value : EMPTY_AGENCY_VALUE
                    }
                    label="Выбор компании"
                    error={!!error}
                    helperText={error?.message}
                    options={agencyOptions}
                    disabled={!hasAvailableAgencies}
                    onChange={(v) => {
                      const value = v.target.value as
                        | EAgencies
                        | typeof EMPTY_AGENCY_VALUE;

                      if (value === EMPTY_AGENCY_VALUE) {
                        field.onChange("");

                        return;
                      }

                      field.onChange(value);

                      setValue(
                        "registration_country_id",
                        agenciesCountry[value as EAgencies] || ECountries.BY,
                      );
                    }}
                  />
                )}
                rules={{
                  validate: (value) => {
                    if (!hasAvailableAgencies) return true;

                    const agencyExists = filteredAgency.some(
                      ({ value: agencyValue }) => agencyValue === value,
                    );

                    return agencyExists || messages.required;
                  },
                }}
              />
            </Box>

            <Box>
              <Controller
                control={control}
                name="organization_form"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <SelectField<EOrganizationValue | null>
                    inputRef={ref}
                    fullWidth
                    {...field}
                    onChange={(v) => {
                      field.onChange(v);
                      handleOrgFormChange(
                        v.target.value as EOrganizationValue | null,
                      );
                    }}
                    label="Вид деятельности"
                    value={field.value}
                    error={!!error}
                    helperText={error?.message}
                    options={reorderedOrganization_form_list}
                  />
                )}
                rules={{ required: messages.required }}
              />
            </Box>
            <Box>
              <Controller
                control={control}
                name="registration_country_id"
                render={({
                  field: { ref, ...field },
                  fieldState: { error },
                }) => (
                  <SelectField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    label="Страна регистрации"
                    error={!!error}
                    helperText={error?.message}
                    options={country}
                  />
                )}
                rules={{ required: messages.required }}
              />
            </Box>
            <Box>
              <Controller
                name="is_resident"
                render={({ field }) => (
                  <FormControlLabel
                    disabled
                    control={<Switch {...field} checked={!!field.value} />}
                    label="Резидент"
                  />
                )}
              />
            </Box>
          </Stack>
        </Box>
      </FormProvider>
    </ModalComponent>
  );
};
